import React, { useState, useEffect } from 'react';
import { Typography, FlexGrid, Notification } from '@telus-uds/components-web';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

import { reportsAPI, exportReportExcel, exportReportCsv } from '../utils/apiService';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import {
  formatNumber,
  formatFileSize,
  formatMilliseconds,
  formatPercentage,
  getDefaultDateRange,
  getPerformanceColor
} from '../utils/formatters';
import SummaryCard from '../components/SummaryCard';
import { orderTypeName } from '../utils/orderTypeLabels';
import ChartDescription from '../components/ChartDescription';
import LastUpdated from '../components/LastUpdated';
import BaselineBadge from '../components/BaselineBadge';
import ShareBarChart, { toShareData } from '../components/ShareBarChart';
import FilterPanel from '../components/FilterPanel';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import DataTable from '../components/DataTable';
import DrillDownModal from '../components/DrillDownModal';
import { chartCardStyle } from '../utils/chartStyles';

const PdfActivityReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [drillDownType, setDrillDownType] = useState(null);
  const [filterValues, setFilterValues] = useState({
    startDate: getDefaultDateRange().startDate,
    endDate: getDefaultDateRange().endDate,
    orderType: '',
    pdfType: '',
    language: '',
    userId: ''
  });
  const [appliedFilters, setAppliedFilters] = useState(null);

  // refresh=true asks the server to skip its cached result (Refresh button).
  const fetchData = async (customFilters = null, refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const filters = customFilters || filterValues;
      setAppliedFilters(filters);
      const result = await reportsAPI.getPdfActivity(refresh ? { ...filters, refresh: true } : filters);
      setData(result.data);
      setMeta({ generatedAt: result.generated_at, throttled: Boolean(result.refresh_throttled) });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to fetch PDF activity data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilterChange = (name, value) => {
    setFilterValues(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    fetchData();
  };

  const handleResetFilters = () => {
    const defaultFilters = {
      startDate: getDefaultDateRange().startDate,
      endDate: getDefaultDateRange().endDate,
      orderType: '',
      pdfType: '',
      language: '',
      userId: ''
    };
    setFilterValues(defaultFilters);
    // Fetch data immediately with default filters
    fetchData(defaultFilters);
  };

  // Uses the applied-filters snapshot, never unapplied edits (same rule as the Excel export).
  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      await exportReportCsv('/reports/pdf-activity/export/csv', appliedFilters || filterValues, 'pdf-activity');
    } catch (err) {
      alert('Failed to export CSV file. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const exportFilters = appliedFilters || filterValues;
      const fileName = `pdf-activity-report-${exportFilters.startDate}-to-${exportFilters.endDate}.xlsx`;
      await exportReportExcel('/reports/pdf-activity/export/excel', exportFilters, fileName);
    } catch (err) {
      alert('Failed to export Excel file. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const filters = [
    {
      name: 'startDate',
      label: 'Start Date',
      type: 'date',
      value: filterValues.startDate,
      placeholder: 'YYYY-MM-DD'
    },
    {
      name: 'endDate',
      label: 'End Date',
      type: 'date',
      value: filterValues.endDate,
      placeholder: 'YYYY-MM-DD'
    },
    {
      name: 'orderType',
      label: 'Order Type',
      type: 'select',
      value: filterValues.orderType,
      options: [
        { value: '', label: 'All' },
        { value: 'BCON', label: 'BCON' },
        { value: 'IOT', label: 'IoT' },
        { value: 'BA', label: 'Business Apps' },
        { value: 'CC', label: 'Contact Centre' }
      ]
    },
    {
      name: 'pdfType',
      label: 'PDF Type',
      type: 'select',
      value: filterValues.pdfType,
      options: [
        { value: '', label: 'All' },
        { value: 'QUOTE', label: 'Quote' },
        { value: 'CONTRACT', label: 'Contract' }
      ]
    },
    {
      name: 'language',
      label: 'Language',
      type: 'select',
      value: filterValues.language,
      options: [
        { value: '', label: 'All' },
        { value: 'en-US', label: 'English' },
        { value: 'fr-CA', label: 'French' }
      ]
    }
  ];

  if (loading) {
    return (
      <div>
        <FilterPanel filters={filters} onFilterChange={handleFilterChange} onApply={handleApplyFilters} onReset={handleResetFilters} />
        <LoadingSpinner text="Loading PDF Activity Report..." />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        <FilterPanel filters={filters} onFilterChange={handleFilterChange} onApply={handleApplyFilters} onReset={handleResetFilters} />
        <Notification variant={{ validation: 'error' }}>
          <div>
            <strong>Error:</strong> {error}
          </div>
        </Notification>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <FilterPanel filters={filters} onFilterChange={handleFilterChange} onApply={handleApplyFilters} onReset={handleResetFilters} />
        <EmptyState title="No Data Available" />
      </div>
    );
  }

  const { summary, by_type, by_order_type, by_language, status, download_vs_view, performance, file_size, hourly_distribution, top_users } = data;

  // Calculate success rate
  const successItem = status && status.find(s => s.status === 'SUCCESS');
  const failedItem = status && status.find(s => s.status === 'FAILED');
  const successCount = parseInt((successItem && successItem.count) || 0, 10);
  const failedCount = parseInt((failedItem && failedItem.count) || 0, 10);
  const totalPdfs = (summary && summary.total_pdfs_generated) || 0;
  const successRate = totalPdfs > 0 ? ((successCount / totalPdfs) * 100).toFixed(2) : 0;

  // Prepare chart data
  const pdfTypeData = by_type && by_type.map(item => ({
    name: item.pdf_type || 'Unknown',
    value: parseInt(item.count, 10)
  })) || [];

  const orderTypeData = by_order_type && by_order_type.map(item => ({
    name: orderTypeName(item.order_type) || 'Unknown',
    count: parseInt(item.count, 10)
  })) || [];

  const languageData = by_language && by_language.map(item => ({
    name: item.language === 'en-US' ? 'English' : item.language === 'fr-CA' ? 'French' : (item.language || 'Unknown'),
    value: parseInt(item.count, 10)
  })) || [];

  const hourlyData = hourly_distribution && hourly_distribution.map(item => ({
    hour: `${item.hour}:00`,
    count: parseInt(item.count, 10)
  })) || [];

  // Prepare top users table data
  const topUsersTableData = top_users && top_users.map((item, index) => {
    const firstName = (item.user && item.user.first_name) || '';
    const lastName = (item.user && item.user.last_name) || '';
    const fullName = `${firstName} ${lastName}`.trim();
    const pdfCount = item.count || item.count === 0 ? parseInt(item.count, 10) : 0;
    
    return {
      id: item.user_id || index,
      rank: index + 1,
      name: fullName || 'Unknown User',
      email: (item.user && item.user.email) || 'N/A',
      count: formatNumber(pdfCount)
    };
  }) || [];

  const topUsersColumns = [
    { key: 'rank', label: 'Rank' },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'count', label: 'PDF Count' }
  ];

  return (
    <div>
      {error && (
        <Notification variant={{ validation: 'error' }}>
          <div>
            <strong>Error:</strong> {error}
          </div>
        </Notification>
      )}
      <FilterPanel
        filters={filters}
        onFilterChange={handleFilterChange}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        onExport={handleExportExcel}
        onExportCsv={handleExportCsv}
        isExporting={isExporting}
      />

      <LastUpdated
        generatedAt={meta && meta.generatedAt}
        throttled={Boolean(meta && meta.throttled)}
        loading={loading}
        onRefresh={() => fetchData(null, true)}
      />

      {/* Summary Cards */}
      <FlexGrid>
        <FlexGrid.Row>
          <FlexGrid.Col xs={12} md={6} lg={3} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
            <SummaryCard
              title="Total PDFs Generated" descriptionKey="totalPdfs"
                extra={<BaselineBadge metric={data.metrics && data.metrics.pdfs_generated} baseline={data.baseline} />}
              value={formatNumber(totalPdfs)}
              icon="📄"
            />
          </FlexGrid.Col>
          <FlexGrid.Col xs={12} md={6} lg={3} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
            <SummaryCard
              title="Success Rate" descriptionKey="pdfSuccessRate"
                extra={<BaselineBadge metric={data.metrics && data.metrics.pdf_success_rate} baseline={data.baseline} />}
              value={`${successRate}%`}
              color={successRate >= 95 ? '#00A600' : '#FF9900'}
              badge={{
                text: `${failedCount} failed`,
                background: failedCount > 0 ? '#ffebee' : '#e8f5e9',
                color: failedCount > 0 ? '#CC0000' : '#00A600'
              }}
            />
          </FlexGrid.Col>
          <FlexGrid.Col xs={12} md={6} lg={3} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
              <SummaryCard
                title="Avg Generation Time" descriptionKey="avgGenerationTime"
                value={formatMilliseconds(performance && performance.avg_generation_time_ms)}
                color={getPerformanceColor(performance && performance.avg_generation_time_ms)}
                subText={`Min: ${formatMilliseconds(performance && performance.min_generation_time_ms)} | Max: ${formatMilliseconds(performance && performance.max_generation_time_ms)}`}
              />
            </FlexGrid.Col>
            <FlexGrid.Col xs={12} md={6} lg={3} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
              <SummaryCard
                title="Total File Size" descriptionKey="totalFileSize"
                value={formatFileSize(file_size && file_size.total_size_bytes)}
                subText={`Avg: ${formatFileSize(file_size && file_size.avg_size_bytes)} per PDF`}
                icon="💾"
              />
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>

      {/* Charts Section */}
      <FlexGrid style={{ marginTop: '2rem' }}>
        <FlexGrid.Row>
          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                PDF Types Distribution
              </Typography>
              <ChartDescription descriptionKey="pdfTypes" />
              <ShareBarChart data={toShareData(pdfTypeData)} title="PDF Types Distribution" onBarClick={setDrillDownType} />
            </div>
          </FlexGrid.Col>

          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Order Types
              </Typography>
              <ChartDescription descriptionKey="pdfOrderTypes" />
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={orderTypeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#0066CC" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </FlexGrid.Col>
        </FlexGrid.Row>

        <FlexGrid.Row>
          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Language Distribution
              </Typography>
              <ChartDescription descriptionKey="pdfLanguage" />
              <ShareBarChart data={toShareData(languageData)} title="Language Distribution" />
            </div>
          </FlexGrid.Col>

          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Hourly Distribution
              </Typography>
              <ChartDescription descriptionKey="pdfHourly" />
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#4b286d"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>

      {/* Top Users Table - Removed as per user request */}

      <DrillDownModal
        open={!!drillDownType}
        onClose={() => setDrillDownType(null)}
        title={`PDFs — ${drillDownType}`}
        queryParams={{
          startDate: filterValues.startDate,
          endDate: filterValues.endDate,
          pdfType: drillDownType,
          orderType: filterValues.orderType,
          language: filterValues.language
        }}
        fetchRecords={reportsAPI.getPdfActivityRecords}
        columns={[
          { key: 'order_id', label: 'Order ID' },
          { key: 'pdf_type', label: 'PDF Type' },
          { key: 'order_type', label: 'Order Type' },
          { key: 'language', label: 'Language' },
          { key: 'status', label: 'Status' },
          { key: 'generation_time_ms', label: 'Gen. Time (ms)' },
          { key: 'created_at', label: 'Created At' }
        ]}
      />
    </div>
  );
};

export default PdfActivityReport;
